import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import StationFormModal from './StationFormModal';
import BuildInstallerModal from './BuildInstallerModal';
import LiteBundleModal from './LiteBundleModal';
import { buildInstallerTarget } from './buildInstallerTarget';
import StationSpecModal from './StationSpecModal';
import AddNodeModal from './AddNodeModal';
import NodeEditModal from './NodeEditModal';
import { useProtectedResources } from './useProtectedResources';
// Shield badge for access-controlled assets (shared with TopologyView).
// SVGR React component; rendered inline (HTML context) inside a titled
// <span> for the hover tooltip. Source: aveq-admin/web/src/shield.svg.
import { ReactComponent as ShieldIcon } from './shield.svg';

// fleet-version-report: presentation for the node's version standing. The BFF
// (_api_node_detail) probes the node and stamps `version_status` (the WORST of the
// jar/python tiers) + `minors_behind`, measured against the mesh's current version.
// `unknown` intentionally has no badge — the version line shows "unavailable" instead.
//
// version-badge-no-verdict: the badges carry NO VERDICT TEXT — no "up to date", no
// "critically out of date". Colour and icon still tier it at a glance, and the suffix
// still reports the MEASURE ("3 behind", "9.8d on version"). What is gone is the
// editorial claim about severity.
//
// WHY: the verdict can be flatly wrong, and was. Prod's furthest-behind node
// (3924802680286881832, v1.73.0 against a v1.191.0 mesh — 18 releases) evaluated to
// `out_of_date = false` and would have rendered "up to date" in green. Both criteria
// missed it: `critical` has never fired because every deploy is recorded `routine`, and
// `age` compares against a 60-day threshold whose clock starts when TRACKING began, not
// when the node adopted the version. Fixing the criteria is
// [[fleet-staleness-criteria]]; this is the presentational half — state what was
// measured, let the reader judge severity. A wrong number invites a second look; a
// confident wrong sentence does not.
const VERSION_BADGE = {
  current:   { icon: '✓', label: '', bg: '#e6f4ea', border: '#9bd3ac', color: '#137333', title: 'Matches the mesh version' },
  supported: { icon: '',  label: '', bg: '#f1f5fb', border: '#c7d2fe', color: '#3730a3', title: 'One minor behind the mesh version' },
  outdated:  { icon: '⚠', label: '', bg: '#fff4e5', border: '#f0c27a', color: '#8a5a00', title: 'Behind the mesh version by more than the support window' },
  critical:  { icon: '🔴', label: '', bg: '#fce8e6', border: '#e6a29a', color: '#a50e0e', title: 'Behind the mesh version by several minors' },
  ahead:     { icon: '↑', label: '', bg: '#eef0f4', border: '#c7ccd6', color: '#556', title: 'Newer than the mesh version (pre-release / dev build)' },
  // fleet-lite-node-versions: a lite node reports its FIRMWARE release, which moves only when
  // lite code changes — so it is deliberately not tiered against the mesh. It needs its own
  // badge rather than falling through to no badge at all: "nothing shown" would read as
  // nothing wrong, and the whole point of tracking these boards is that they were invisible.
  lite:      { icon: '⚙', label: 'not compared',            bg: '#f4f1fb', border: '#cfc7e6', color: '#4b3a7a', title: 'Lite-node firmware. Not compared to the mesh version: it only changes when lite code does, so a release-cadence badge would flag a current board with no way to clear it. Compare the version with the repo.' },
};

// root-fleet-helper: "3h ago" / "2d ago" for the root's last_seen stamp.
function fmtAgo(iso) {
  if (!iso) return 'unknown';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return 'unknown';
  const m = Math.round(ms / 60000);
  if (m < 2) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// fleet-version-report: node software version + out-of-date badge.
// root-fleet-helper: the source is now the network ROOT's fleet inventory
// (`version_source: 'fleet'` — last-known version, `version_last_seen`, and the
// root's age-on-version verdict), which is why this also renders for an OFFLINE
// node. The BFF falls back to a live admin:probe (`version_source: 'probe'`,
// minor-delta tiers) for a node the root hasn't collected yet.
function VersionRow({ detail }) {
  const fromFleet = detail.version_source === 'fleet';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  margin: '0 0 16px', fontSize: '0.8rem', color: '#555' }}
         data-testid="version-row" data-version-source={detail.version_source || 'none'}>
      <span style={{ color: '#888' }}>version</span>
      {(detail.jar_version || detail.python_version) ? (
        <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
          qbric {detail.jar_version || '—'}
          {detail.python_version && detail.python_version !== detail.jar_version
            ? ` · py ${detail.python_version}` : ''}
        </span>
      ) : (
        <span style={{ fontStyle: 'italic', color: '#999' }}>unavailable (node not reachable)</span>
      )}
      {fromFleet && detail.version_last_seen && (
        <span style={{ color: '#999' }} title={`Root last saw this node at ${detail.version_last_seen}`}>
          seen {fmtAgo(detail.version_last_seen)}
        </span>
      )}
      {(() => {
        const b = VERSION_BADGE[detail.version_status];
        if (!b) return null;
        // version-badge-no-verdict: the MEASURE only. Labels are empty now, so a
        // measure carrying its own ' · ' would dangle after the icon; they are
        // joined at render instead.
        let measure = '';
        let title = b.title;
        if (fromFleet) {
          // Badge = worse of the root's AGE verdict and the mesh minor-DELTA tier;
          // `version_reason` names which fired, `measure` reports it.
          const age = detail.version_age_days;
          const behind = detail.minors_behind;
          if (detail.version_reason === 'critical') {
            // deploy-registry-core: the root says this node missed a CRITICAL deploy.
            // "behind critical 1.155.0" read as "critically behind" — the adjective this
            // slug removed, smuggled back in by word order, and it did not match the
            // chat surface. Name the DEPLOY and how it was recorded; attach no
            // adjective to the node.
            measure = detail.version_critical_deploy
              ? `below critical deploy v${detail.version_critical_deploy}` : '';
            title = `Version is below deploy ${detail.version_critical_deploy || '?'}, which was recorded critical (root's registry)`;
          } else if (detail.version_reason === 'not-evaluated') {
            // A lite row: say what IS known (when the root last saw it) and nothing implying
            // a comparison was made.
            measure = (age != null) ? `${Math.round(age)}d on this firmware` : '';
            title = b.title;
          } else if (detail.version_reason === 'delta') {
            measure = (typeof behind === 'number') ? `${behind} behind` : '';
            title = `${behind} minor version(s) behind the mesh (root says ${age != null ? `${Math.round(age)}d` : '?'} on this version — first seen by the inventory, not necessarily when it fell behind)`;
          } else {
            measure = (age != null) ? `${Math.round(age)}d on version` : '';
            title = detail.version_status === 'outdated'
              ? `${Math.round(age)}d on this version, past the root's ${detail.stale_after_days}d threshold (adopted ${detail.version_adopted_at || '?'} — when the inventory first recorded it, not necessarily when the node adopted it)`
              : `${age != null ? `${Math.round(age)}d` : '?'} on this version, within the root's ${detail.stale_after_days}d threshold (adopted ${detail.version_adopted_at || '?'})`;
          }
        } else {
          const behind = detail.minors_behind;
          // !758 review: `0 behind` rather than nothing, so a level node's badge is not a
          // bare icon that reads as broken/empty. Still a measure, not a verdict.
          measure = (typeof behind === 'number') ? `${behind} behind` : '';
        }
        if (detail.version_current) title += ` (mesh ${detail.version_current})`;
        return (
          <span title={title}
                style={{ fontSize: '0.7rem', fontWeight: 600, borderRadius: 3,
                         padding: '1px 6px', background: b.bg,
                         border: `1px solid ${b.border}`, color: b.color }}>
            {b.icon ? `${b.icon} ` : ''}{[b.label, measure].filter(Boolean).join(' · ')}
          </span>
        );
      })()}
    </div>
  );
}

// ota-bare-metal-self-update: what the menu item promises has to match what the
// node will actually do. On a `manual` node "update to v1.194.0" is a lie — the
// node takes a note and nothing else — and a label that overstates gets read as a
// broken updater rather than as the posture its owner chose.
export function otaActionLabel(ota) {
  if (ota && ota.mode === 'mesh' && ota.self_update !== 'auto') {
    return `notify of ${versionLabel(ota.target_tag)}`;
  }
  return `update to ${versionLabel(ota.target_tag)}`;
}

// A docker target is the tag pinned in `.env` and may already carry its v; a mesh target doesn't.
export function versionLabel(tag) {
  return `v${String(tag ?? '').replace(/^v/, '')}`;
}

// Alphabetical, case-insensitive; id breaks ties so the order is stable.
export function sortStations(stations) {
  return [...stations].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    || Number(a.id) - Number(b.id));
}

export default function NodeDetail({ nodeId, networkId, networkCanonical, onProbeOk, probedOkIds, onDeleted, stationPanelHost }) {
  // protected-asset-indicator slug: shield badges on access-controlled
  // node header + stations. refresh() re-pulls after a Protect change.
  const { isProtected, refresh: refreshProtected } = useProtectedResources();
  // Per-network-topology-source slug: when a non-legacy network is
  // selected, /api/nodes/{id} needs to route to that network's qbric
  // (the legacy root-qbric only knows aveq-1 / aveq nodes). Same shape
  // the topology fetch uses — `?network=<canonical>` query.
  const apiNodePath = (id) => networkCanonical
    ? `/api/nodes/${id}?network=${encodeURIComponent(networkCanonical)}`
    : `/api/nodes/${id}`;
  // node-ota-update: a SUB-PATH of the node resource. Not `apiNodePath(id) + '/x'`
  // — that appends after the query string and yields
  // `/api/nodes/5?network=aveq/updatable`, which 404s silently and leaves the
  // action absent with nothing in the console to explain it. Cost a live-verify
  // round: the bundle had the code, the BFF returned updatable:true, and the
  // menu item still never appeared.
  const apiNodeSubPath = (id, sub) => networkCanonical
    ? `/api/nodes/${id}/${sub}?network=${encodeURIComponent(networkCanonical)}`
    : `/api/nodes/${id}/${sub}`;
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // topology-pending-nodes slug: 404 from /api/nodes/{id} means qbric's
  // routing graph doesn't know this node yet — it's been provisioned in
  // hub PG (so it shows as a pending circle in TopologyView) but the
  // recipient hasn't booted + connected. Render a quiet placeholder
  // rather than the raw "Error: HTTP 404" string.
  const [isPending, setIsPending] = useState(false);
  // root-fleet-helper: a 404 whose body carries `offline: true` is a node the
  // network root HAS seen before — its last-known version rides along from the
  // root's fleet inventory. Rendered as "Offline — last known …" instead of the
  // never-connected "Pending" placeholder.
  const [offlineFleet, setOfflineFleet] = useState(null);
  const [probe, setProbe] = useState(null);    // { loading, result?, error?, target }
  // CRUD modal state. `editing` is the station object being edited
  // (null for Add). `confirmDelete` holds the station awaiting a typed-
  // name confirmation; `confirmText` is what the operator has typed.
  const [showStationForm, setShowStationForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  // Node decommission (decommission slug): typed-name confirm + the two
  // cascade toggles forwarded as query params to DELETE /api/nodes/{id}.
  const [confirmNodeDelete, setConfirmNodeDelete] = useState(false);
  // node-ota-update (ADR 2026-09-21): eligibility is asked, never assumed. The
  // ACTION IS ABSENT unless the BFF says this host can see the node's container —
  // a disabled button would imply the operator could do something about an
  // in-stack node, a lite device, or a worker on someone else's box. They can't.
  const [ota, setOta] = useState(null);           // {updatable, reason, target_tag, ...}
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [updateText, setUpdateText] = useState('');
  const [updating, setUpdating] = useState(null); // null | 'busy' | {ok, msg}
  // Outcome shown in the PANEL, not the modal, so it outlives the dialog.
  const [otaFlash, setOtaFlash] = useState(null);  // null | {ok, msg}
  const otaTimers = useRef([]);
  // Consumed ONCE by the next detail fetch. The post-update refetch must ask the
  // root to re-probe, or it re-reads the same hourly-stale inventory row and the
  // version line still shows the pre-update reading — which reads as "the update
  // did nothing".
  const recollectOnce = useRef(false);
  useEffect(() => () => {
    // Same reason `restartTimers` exists: an 8s refetch that fires after the
    // selected node changed would refetch the WRONG node and clear its state.
    otaTimers.current.forEach(clearTimeout);
    otaTimers.current = [];
  }, [nodeId]);
  const [nodeConfirmText, setNodeConfirmText] = useState('');
  const [delInclStations, setDelInclStations] = useState(true);
  const [delInclMember, setDelInclMember] = useState(true);
  const [deletingNode, setDeletingNode] = useState(false);
  // admin-node-station-edit slug: the flat Info modal is replaced by the
  // unified NodeEditModal (SectionedDialog). It owns its own rename + loaded-
  // helpers state internally, so NodeDetail only tracks open/closed here.
  const [showEdit, setShowEdit] = useState(false);
  const [showBuildInstaller, setShowBuildInstaller] = useState(false);
  // lite-build-installer: separate state for the lite re-mint modal.
  // BuildInstallerModal hits bin/build-installer.sh which doesn't know
  // about the lite substrate; LiteBundleModal hits the lite-specific
  // BFF route that reuses existing credentials.
  const [showLiteBundle, setShowLiteBundle] = useState(false);
  // multi-network slug Phase 4: "Add Child Node" entry replaces the
  // previously global + Add Node button on the topology toolbar.
  const [showAddChild, setShowAddChild] = useState(false);
  const [specStation, setSpecStation] = useState(null);
  const [selectedStationId, setSelectedStationId] = useState(null);
  const [openSection, setOpenSection] = useState('stations');
  useEffect(() => { setSelectedStationId(null); }, [nodeId]);
  // Bump after any CRUD action to force a refetch of NodeDetail.
  const [refetchKey, setRefetchKey] = useState(0);
  // core-network-tiers slug: in-stack container lifecycle (start/stop/restart).
  const [lifecycle, setLifecycle] = useState(null); // {busy, action, ok, msg}
  // standalone-worker-remote-restart review #7: track the flash-clear + delayed
  // refetch timers runRemoteRestart schedules so they can be cancelled when the
  // selected node changes (or the panel unmounts). Otherwise an 8s refetch fires
  // against whatever node is now selected and dismisses its open probe modal.
  const restartTimers = useRef([]);
  useEffect(() => () => {
    restartTimers.current.forEach(clearTimeout);
    restartTimers.current = [];
  }, [nodeId]);

  // Eligibility rides its own request: it needs the docker socket, so it is slower
  // and far more likely to fail than the node detail — and a panel that fails to
  // load because an unrelated daemon is down would be a bad trade.
  useEffect(() => {
    setOta(null); setConfirmUpdate(false); setUpdateText(''); setUpdating(null);
    if (!nodeId) return undefined;
    let live = true;
    fetch(apiNodeSubPath(nodeId, 'updatable'))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (live) setOta(j); })
      .catch(() => {});
    return () => { live = false; };
  }, [nodeId, refetchKey]);

  const submitUpdate = async () => {
    // The modal STAYS OPEN for the whole run. The request is a container
    // recreate — it can take tens of seconds on a cold prod image — and a dialog
    // that vanishes on click leaves the operator watching an unchanged panel
    // with no way to tell "working" from "did nothing". The first version did
    // exactly that AND dropped its own success message, because the message only
    // rendered inside the modal it had already closed.
    setUpdating('busy');
    setOtaFlash(null);
    try {
      const r = await fetch(apiNodeSubPath(nodeId, 'update'), { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) {
        // Outcome goes to the panel flash (the shape `lifecycle` already uses),
        // so it survives the modal closing and sits next to the version row it
        // is about.
        setOtaFlash({ ok: true,
                      msg: `updated ${j.previous_tag || '?'} \u2192 ${versionLabel(j.target_tag)}; restarting\u2026` });
        setUpdating(null);
        setConfirmUpdate(false);
        // The worker takes a few seconds to come back; re-pull so the version
        // row reflects it rather than the pre-update reading. The flash says
        // "restarting…" until then, so the stale row is explained rather than
        // just wrong.
        otaTimers.current.push(setTimeout(() => {
          recollectOnce.current = true;
          setRefetchKey((k) => k + 1);
          setOtaFlash({ ok: true, msg: `updated to ${versionLabel(j.target_tag)}` });
        }, 8000));
        otaTimers.current.push(setTimeout(() => setOtaFlash(null), 15000));
      } else {
        // Failure keeps the modal: the error is long (compose stderr) and the
        // operator is mid-decision, not done.
        setUpdating({ ok: false, msg: j.error || `HTTP ${r.status}` });
      }
    } catch (e) {
      setUpdating({ ok: false, msg: String(e) });
    }
  };

  useEffect(() => {
    if (!nodeId) { setDetail(null); setProbe(null); setIsPending(false); setOfflineFleet(null); return; }
    let cancelled = false;
    setLoading(true);
    setProbe(null);  // clear any prior probe result on selection change
    setIsPending(false);
    setOfflineFleet(null);
    (async () => {
      try {
        const wantFresh = recollectOnce.current;
        recollectOnce.current = false;
        const r = await fetch(wantFresh
          ? apiNodePath(nodeId) + (networkCanonical ? '&' : '?') + 'recollect=1'
          : apiNodePath(nodeId));
        if (r.status === 404) {
          // Pending node — known to hub but not to the live qbric routing
          // graph. Show a quiet placeholder; not an error. If the root's fleet
          // inventory knows the node (`offline`), keep that body for the placeholder.
          let body = null;
          try { body = await r.json(); } catch (_) { /* qbric's raw 404 has no JSON body */ }
          if (!cancelled) {
            setIsPending(true); setDetail(null); setError(null);
            setOfflineFleet(body && body.offline ? body : null);
          }
          return;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        if (!cancelled) { setDetail(await r.json()); setError(null); }
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nodeId, refetchKey, networkCanonical]);

  const toggleStationStatus = async (station) => {
    const next = (station.status === 'ACTIVE') ? 'INACTIVE' : 'ACTIVE';
    try {
      const r = await fetch(`/api/stations/${station.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      });
      if (!r.ok) { const t = await r.text(); throw new Error(t); }
      setRefetchKey((k) => k + 1);
    } catch (e) { setError(String(e.message || e)); }
  };

  const submitDelete = async (station) => {
    try {
      const r = await fetch(`/api/stations/${station.id}`, { method: 'DELETE' });
      if (!r.ok) { const t = await r.text(); throw new Error(t); }
      setConfirmDelete(null); setConfirmText('');
      setRefetchKey((k) => k + 1);
    } catch (e) { setError(String(e.message || e)); }
  };

  const submitNodeDelete = async () => {
    setDeletingNode(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        include_stations: String(delInclStations),
        include_member: String(delInclMember),
      }).toString();
      const r = await fetch(`/api/nodes/${nodeId}?${qs}`, { method: 'DELETE' });
      if (!r.ok) { const t = await r.text(); throw new Error(t); }
      setConfirmNodeDelete(false);
      // The node is gone; let App deselect this panel AND track the id so
      // TopologyView can hide the node immediately, even while qbric's
      // topology cache is still serving the stale snapshot (cleanup
      // interval can take ~60s to refresh).
      if (onDeleted) onDeleted(nodeId);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setDeletingNode(false);
    }
  };

  const sendProbe = async (target) => {
    setProbe({ loading: true, target });
    try {
      const body = { target_node_id: nodeId };
      if (target.station_id) body.station_id = String(target.station_id);
      // Per-network qbric routing: a peer-network's downstream is only
      // reachable via that network's root qbric. Default (no canonical)
      // hits root-qbric for aveq-1 / aveq nodes.
      const probeUrl = networkCanonical
        ? `/api/probe?network=${encodeURIComponent(networkCanonical)}`
        : '/api/probe';
      const r = await fetch(probeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      if (!r.ok) {
        setProbe({ target, error: parsed?.error || `HTTP ${r.status}: ${text}` });
        return;
      }
      setProbe({ target, result: target.station_id ? withoutImage(parsed) : parsed });
      // Tell the parent so the topology can paint this node green
      // (overrides the stale `live` from /api/topology).
      if (onProbeOk) onProbeOk(nodeId);
    } catch (e) {
      setProbe({ target, error: String(e) });
    }
  };

  // core-network-tiers slug: start/stop/restart the in-stack compose container
  // backing this node (POST /api/nodes/{id}/lifecycle). Only offered when the
  // node carries a compose_target (standalone/remote nodes don't).
  const runLifecycle = async (action) => {
    setLifecycle({ busy: true, action });
    // Hold the result briefly as a visual cue (the button flashes green/red),
    // then clear so the buttons return to rest. Success fades fast; an error
    // lingers long enough to read (these were previously silent).
    const finish = (result) => {
      setLifecycle(result);
      const ttl = result.ok ? 1500 : 6000;
      setTimeout(() => {
        setLifecycle((l) => (l && l.action === result.action && !l.busy ? null : l));
      }, ttl);
    };
    try {
      const r = await fetch(`/api/nodes/${nodeId}/lifecycle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      if (!r.ok) {
        finish({ ok: false, action, msg: parsed?.error || `HTTP ${r.status}: ${text}` });
        return;
      }
      finish({ ok: true, action, msg: `${action} → ${parsed?.state || 'ok'}` });
      setRefetchKey((k) => k + 1);  // re-pull detail so liveness reflects it
    } catch (e) {
      finish({ ok: false, action, msg: String(e) });
    }
  };

  // standalone-worker-remote-restart slug: restart an OFF-STACK standalone
  // worker in place over the mesh (POST /api/nodes/{id}/remote-restart →
  // admin:exec restart). The off-stack sibling of runLifecycle above — admin
  // has no host on the recipient's machine, so it bounces the worker via a
  // mesh control frame the worker ACKS before it re-execs. Reuses the same
  // `lifecycle` flash state (standalone nodes never show the compose_target
  // start/stop buttons, so there's no collision). Shown only for
  // standalone-substrate nodes.
  const runRemoteRestart = async () => {
    const action = 'restart';
    setLifecycle({ busy: true, action });
    const track = (id) => { restartTimers.current.push(id); return id; };
    const finish = (result) => {
      setLifecycle(result);
      const ttl = result.ok ? 2500 : 6000;
      // Clear by result IDENTITY, not action match (review #7): a retry replaces
      // `lifecycle` with a NEW object, so this stale timer only nulls state if
      // it's still showing THIS exact result — it can't wipe a retry's message.
      track(setTimeout(() => setLifecycle((l) => (l === result ? null : l)), ttl));
    };
    try {
      const url = networkCanonical
        ? `/api/nodes/${nodeId}/remote-restart?network=${encodeURIComponent(networkCanonical)}`
        : `/api/nodes/${nodeId}/remote-restart`;
      // BFF synthesizes the admin:exec restart frame itself; it reads nothing
      // from the request body, so none is sent.
      const r = await fetch(url, { method: 'POST' });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      // qbric's /admin/probe nests the worker's reply at payload[0] and returns
      // HTTP 200 even when that inner reply is a refusal — so we inspect the
      // inner ack, not just r.ok. Require an explicit inner.ok === true (review
      // #8): an empty/dropped payload leaves inner.ok undefined and must read as
      // failure, not a false "restarting…". A transport failure (worker dead
      // before ack) surfaces as a top-level {error} with a non-200 status.
      const inner = parsed?.payload?.[0] || {};
      if (!r.ok || inner.ok !== true) {
        finish({ ok: false, action,
          msg: inner.error || parsed?.error || `HTTP ${r.status}: ${text}` });
        return;
      }
      const secs = inner.delay_ms ? Math.round(inner.delay_ms / 100) / 10 : null;
      finish({ ok: true, action, msg: secs ? `restarting in ${secs}s…` : 'restarting…' });
      // Bounce takes a few seconds; re-pull detail after so liveness settles.
      track(setTimeout(() => setRefetchKey((k) => k + 1), 8000));
    } catch (e) {
      finish({ ok: false, action, msg: String(e) });
    }
  };

  if (!nodeId) return <div style={{ color: '#888', fontSize: '0.9rem' }}>Click a node to inspect.</div>;
  if (loading && !detail) return <div style={{ color: '#888' }}>Loading…</div>;
  if (isPending && offlineFleet) return (
    <div style={{ color: '#a06000', fontSize: '0.9rem' }} data-testid="node-offline">
      <div>Offline — last seen {fmtAgo(offlineFleet.version_last_seen)}.</div>
      <VersionRow detail={offlineFleet} />
    </div>
  );
  if (isPending) return (
    <div style={{ color: '#a06000', fontSize: '0.9rem' }}>
      Pending — provisioned but not yet connected to the mesh.
      Run the installer on the recipient (or start the spawned container) to bring it online.
    </div>
  );
  if (error) return <div style={{ color: '#c00', fontSize: '0.85rem' }}>Error: {error}</div>;
  if (!detail) return null;

  const probeFor = (target) => probe && probe.target.kind === target.kind
    && probe.target.station_id === target.station_id ? probe : null;
  const selectedStation = (detail.stations || [])
    .find((st) => String(st.id) === String(selectedStationId)) || null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 6 }}>
        <>
            <h2 style={{ margin: 0, fontSize: '1.05rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isProtected && isProtected('node', detail.nodeId) && (
                <span title="Access-controlled (protected)"
                      style={{ marginRight: 6, display: 'inline-flex', verticalAlign: 'middle' }}>
                  <ShieldIcon width={14} height={14} />
                </span>
              )}
              {detail.name || '(unnamed)'}
              {detail.lite && (
                /* lite-node slug Phase 5: surface plaintext-via-rendezvous
                   nodes so operators see what's enveloped end-to-end
                   and what isn't. Sourced from rendezvous's /lite-nodes
                   feed; threaded into /api/topology by the BFF. */
                <span title="Lite node — plaintext via rendezvous; no envelope crypto. See lite-node ADR."
                      style={{ marginLeft: 8, fontSize: '0.7rem', color: '#666',
                               background: '#f4ead8', border: '1px solid #d8c89c',
                               borderRadius: 3, padding: '1px 5px', verticalAlign: 'middle' }}>
                  🪶 lite
                </span>
              )}
            </h2>
            {/* Non-primary node actions. Build installer surfaces the .run
                for any node provisioned with an app-identity (standalone
                substrate or `bin/provision-node.sh --app-identity`); errors
                at request time if the node has none (412) or ambiguous (409).
                lite-build-installer: the BFF stamps `has_app_identity` on
                the detail response so we hide the item up-front for nodes
                that would just 412 (in-stack admin/agent, pre-app-identity
                provisions). Lite nodes get their own re-mint surface via
                LiteBundleModal — gated on `detail.lite` separately. */}
            <HamburgerMenu items={[
              { label: 'add child node', onClick: () => setShowAddChild(true) },
              { label: 'add station', onClick: () => { setEditing(null); setShowStationForm(true); } },
              { label: 'edit', onClick: () => setShowEdit(true) },
              // Absent unless this host can act. See the `ota` state comment.
              ...(ota && ota.updatable
                ? [{ label: otaActionLabel(ota),
                     onClick: () => { setUpdateText(''); setUpdating(null); setConfirmUpdate(true); } }]
                : []),
              ...(() => {
                // lite-substrate-marker (v0.106.1): routing logic
                // lives in buildInstallerTarget(detail) so the
                // contract is unit-testable as a pure function.
                const target = buildInstallerTarget(detail);
                if (!target) return [];
                return [{
                  label: 'build installer',
                  onClick: () => target === 'lite'
                    ? setShowLiteBundle(true)
                    : setShowBuildInstaller(true),
                }];
              })(),
              { divider: true },
              { label: 'delete node', danger: true, onClick: () => {
                  setConfirmNodeDelete(true); setNodeConfirmText('');
                  setDelInclStations(true); setDelInclMember(true);
                } },
            ]} />
        </>
      </div>
      <VersionRow detail={detail} />
      {/* core-network-tiers slug: node actions — probe always; the in-stack
          lifecycle (start/stop/restart) only when the node maps to an
          admin-managed compose container (compose_target). Standalone/remote
          nodes show just Probe. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 24px' }}>
        <button type="button" onClick={() => sendProbe({ kind: 'node' })} style={probeBtn}>probe</button>
        {detail.compose_target && [['start', '▶'], ['stop', '⏸']].map(([a, icon]) => {
          const on = lifecycle?.action === a;
          const busy = on && lifecycle.busy;
          const done = on && !lifecycle.busy;  // result is flashing
          const flash = busy
            ? { background: '#3b6bb1' }                                  // in-progress: blue
            : done
              ? { background: lifecycle.ok ? '#2e8b57' : '#c0392b' }      // ok: green / fail: red
              : null;
          return (
            <button key={a} type="button" title={a} disabled={!!lifecycle?.busy}
              onClick={() => runLifecycle(a)}
              style={{ ...probeBtn, minWidth: 32, transition: 'background 0.2s ease', ...flash }}>
              {busy ? '…' : done ? (lifecycle.ok ? '✓' : '✕') : icon}
            </button>
          );
        })}
        {/* standalone-worker-remote-restart slug: a "restart" for OFF-STACK
            standalone workers. In-stack nodes get start/stop above (admin owns
            their compose host); standalone workers have no host admin can reach,
            so this bounces them over the mesh via admin:exec restart. A worker
            that doesn't enable remote restart (e.g. bare-metal, for now) refuses
            with a clean error surfaced in the status line.
            Gate on buildInstallerTarget(detail) === 'standalone' (review #4), not
            detail.substrate directly: the substrate marker is only stamped at
            provision time (v0.106.1+), so the aging pre-fix standalone fleet — the
            nodes you most want to bounce without SSH — has substrate=null. The
            shared helper's has_app_identity fallback covers them, and returns null
            for in-stack / lite nodes so the button stays correctly hidden there. */}
        {buildInstallerTarget(detail) === 'standalone' && (() => {
          const on = lifecycle?.action === 'restart';
          const busy = on && lifecycle.busy;
          const done = on && !lifecycle.busy;
          const flash = busy
            ? { background: '#3b6bb1' }
            : done
              ? { background: lifecycle.ok ? '#2e8b57' : '#c0392b' }
              : null;
          return (
            <button type="button" title="restart this worker in place (over the mesh)"
              disabled={!!lifecycle?.busy}
              onClick={runRemoteRestart}
              style={{ ...probeBtn, transition: 'background 0.2s ease', ...flash }}>
              {busy ? '…' : done ? (lifecycle.ok ? '✓ restart' : '✕ restart') : '⟳ restart'}
            </button>
          );
        })()}
        {otaFlash && (
          <span data-testid="ota-flash" style={{
            alignSelf: 'center', fontSize: '0.75rem',
            color: otaFlash.ok ? '#2e8b57' : '#c0392b',
          }}>{otaFlash.msg}</span>
        )}
        {lifecycle && !lifecycle.busy && lifecycle.msg && (
          <span style={{
            alignSelf: 'center', fontSize: '0.75rem',
            color: lifecycle.ok ? '#2e8b57' : '#c0392b',
          }}>{lifecycle.msg}</span>
        )}
      </div>
      <ProbeResult probe={probeFor({ kind: 'node', station_id: undefined })} onClose={() => setProbe(null)} />
      {detail.stations && (
        <AccordionSection title={`Stations (${detail.stations.length})`}
          open={openSection === 'stations'}
          onToggle={() => setOpenSection((o) => (o === 'stations' ? null : 'stations'))}>
          <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
          {detail.stations.length === 0 && <Empty>No stations.</Empty>}
          {sortStations(detail.stations).map((s) => (
            <button key={s.id} type="button" data-station-row
              aria-pressed={String(s.id) === String(selectedStationId)}
              onClick={() => setSelectedStationId(s.id)}
              style={{ ...stationRow,
                       ...(String(s.id) === String(selectedStationId) ? stationRowActive : null) }}>
              <StationTitle station={s} protectedHere={isProtected && isProtected('station', s.id)} />
            </button>
          ))}
          </div>
        </AccordionSection>
      )}
      {detail.apps && (
        <AccordionSection title={`Apps (${detail.apps.length})`}
          open={openSection === 'apps'}
          onToggle={() => setOpenSection((o) => (o === 'apps' ? null : 'apps'))}>
          <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
          {detail.apps.length === 0 && <Empty>No apps registered.</Empty>}
          {detail.apps.map((a) => (
            <div key={a.name} style={item}>
              <strong>{a.name}</strong>
              {a.provider && <span style={meta}> · {a.provider}</span>}
              {!a.active && <span style={{ color: '#c44', marginLeft: 6 }}>(inactive)</span>}
              {a.namespaces && a.namespaces.length > 0 && (
                <div style={{ ...meta, marginLeft: 0, marginTop: 2 }}>
                  namespaces: {a.namespaces.join(', ')}
                </div>
              )}
              {a.description && (
                <div style={{ ...meta, marginLeft: 0, marginTop: 2, fontStyle: 'italic' }}>
                  {a.description}
                </div>
              )}
            </div>
          ))}
          </div>
        </AccordionSection>
      )}
      {selectedStation && stationPanelHost && ReactDOM.createPortal(
        <StationPanel
          key={selectedStation.id}
          station={selectedStation}
          nodeId={nodeId}
          networkId={networkId}
          isProtected={isProtected}
          modalOpen={!!(showStationForm || specStation || confirmDelete || probe)}
          onClose={() => setSelectedStationId(null)}
          onProbe={() => sendProbe({ kind: 'station', station_id: selectedStation.id })}
          menuItems={[
            { label: 'edit', onClick: () => { setEditing(selectedStation); setShowStationForm(true); } },
            { label: 'view spec', onClick: () => setSpecStation(selectedStation) },
            { label: selectedStation.status === 'ACTIVE' ? 'disable' : 'enable',
              onClick: () => toggleStationStatus(selectedStation) },
            { divider: true },
            { label: 'delete', danger: true,
              onClick: () => { setConfirmDelete(selectedStation); setConfirmText(''); } },
          ]}
        />,
        stationPanelHost,
      )}
      <ProbeResult probe={probe && probe.target.kind === 'station' ? probe : null} onClose={() => setProbe(null)} />
      {showStationForm && (
        <StationFormModal
          nodeId={nodeId}
          initial={editing}
          networkCanonical={networkCanonical}
          onClose={() => setShowStationForm(false)}
          onSaved={() => setRefetchKey((k) => k + 1)}
          onAccessChanged={() => { if (refreshProtected) refreshProtected(); }}
        />
      )}
      {/* admin-node-station-edit slug: the flat "Info" modal is replaced by the
          unified NodeEditModal (SectionedDialog) — Name (rename) + Access
          (relocated from the old "Manage Access" ProtectModal) + Helpers. */}
      {showEdit && (
        <NodeEditModal
          nodeId={nodeId}
          detail={detail}
          networkCanonical={networkCanonical}
          onClose={() => setShowEdit(false)}
          onChanged={() => { setRefetchKey((k) => k + 1); if (refreshProtected) refreshProtected(); }}
        />
      )}
      {showBuildInstaller && (
        <BuildInstallerModal
          nodeId={nodeId}
          nodeName={detail.name}
          networkId={networkId}
          onClose={() => setShowBuildInstaller(false)}
        />
      )}
      {showLiteBundle && (
        <LiteBundleModal
          nodeId={nodeId}
          nodeName={detail.name}
          onClose={() => setShowLiteBundle(false)}
        />
      )}
      {showAddChild && (
        <AddNodeModal
          parentNodeId={nodeId}
          parentNodeName={detail.name}
          networkId={networkId}
          onClose={() => setShowAddChild(false)}
          onSuccess={() => {
            // TopologyView's 5s poll picks up the new child; close
            // happens on the modal's Done button (so the operator
            // sees the credentials reveal first).
          }}
        />
      )}
      {specStation && (
        <StationSpecModal
          stationId={specStation.id}
          stationName={specStation.name}
          stationConfig={specStation.config || {}}
          onClose={() => setSpecStation(null)}
        />
      )}
      {confirmDelete && (
        <div style={overlayStyle} onClick={() => setConfirmDelete(null)}>
          <div style={confirmModalStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#c00' }}>Delete station #{confirmDelete.id}</h3>
            <p style={{ fontSize: '0.85rem' }}>
              This permanently removes <strong>{confirmDelete.name}</strong> and cascades through node_station,
              message_unread, station_handle, station_permission, agent_spec. There is no undo.
            </p>
            <p style={{ fontSize: '0.85rem' }}>
              Type the station name <code style={{ background: '#f5f5f5', padding: '1px 4px' }}>{confirmDelete.name}</code> to confirm:
            </p>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                   autoFocus style={{ ...inputStyle, width: '100%', marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmDelete(null)} style={buttonSecondaryStyle}>Cancel</button>
              <button type="button" disabled={confirmText !== confirmDelete.name}
                      onClick={() => submitDelete(confirmDelete)}
                      style={{ ...buttonDangerStyle, opacity: confirmText === confirmDelete.name ? 1 : 0.4 }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmUpdate && detail && ota && ota.updatable && (
        <div style={overlayStyle} onClick={() => updating !== 'busy' && setConfirmUpdate(false)}>
          <div style={confirmModalStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Update node #{detail.nodeId}</h3>
            {ota.mode === 'mesh' ? (
              // ota-bare-metal-self-update (ADR amendment B): NONE of the host-path
              // copy is true here. There is no container to recreate, no tag to pin,
              // no working dir — and, for `manual`/`deferred`, no restart either. The
              // old text would have promised an update where the node will only take
              // a note, which is the kind of wrong that gets reported as a bug in the
              // updater rather than as a misread dialog.
              <>
                <p style={{ fontSize: '0.85rem' }}>
                  Tells <strong>{detail.name}</strong> that{' '}
                  <code style={{ background: '#f5f5f5', padding: '1px 4px' }}>
                    {versionLabel(ota.target_tag)}
                  </code>{' '}
                  exists. What happens next is that node&rsquo;s own setting
                  (<code>{ota.self_update}</code>), which can only be changed on the
                  machine itself:
                </p>
                <p style={{ fontSize: '0.85rem', color: '#444' }}>{ota.note}</p>
                {ota.self_update === 'auto' && (
                  <p style={{ fontSize: '0.85rem', color: '#8a5a00' }}>
                    Live sessions on this node&rsquo;s station(s) will drop while it
                    restarts.
                  </p>
                )}
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.85rem' }}>
                  Recreates <strong>{detail.name}</strong> on{' '}
                  <code style={{ background: '#f5f5f5', padding: '1px 4px' }}>
                    {ota.current_tag || '?'} &rarr; {versionLabel(ota.target_tag)}
                  </code>{' '}
                  and pins the new tag in its <code>.env</code> (previous kept as
                  <code>.env.bak</code>).
                </p>
                <p style={{ fontSize: '0.8rem', color: '#666' }}>
                  Working dir <code>{ota.working_dir}</code>
                </p>
                {/* ADR D4: the operator accepts the interruption explicitly. A worker
                    recreate drops live sessions on its stations — saying so here is
                    the difference between a chosen outage and a surprising one. */}
                <p style={{ fontSize: '0.85rem', color: '#8a5a00' }}>
                  Live sessions on this node&rsquo;s station(s) will drop while it restarts.
                </p>
              </>
            )}
            <p style={{ fontSize: '0.85rem' }}>
              Type the node name <code style={{ background: '#f5f5f5', padding: '1px 4px' }}>{detail.name}</code> to confirm:
            </p>
            <input value={updateText} onChange={(e) => setUpdateText(e.target.value)}
                   autoFocus style={{ ...inputStyle, width: '100%', marginBottom: 12 }} />
            {updating === 'busy' && (
              // Says what is happening and roughly how long, because the two
              // questions an operator has while staring at a dialog are "is it
              // stuck?" and "how long?".
              <p data-testid="ota-progress" style={{ fontSize: '0.8rem', color: '#5fa67a' }}>
                {ota.mode === 'mesh'
                  ? 'Sending\u2026 the node acknowledges before it acts.'
                  : 'Recreating the container\u2026 this usually takes 10\u201330s.'}
              </p>
            )}
            {updating && updating !== 'busy' && !updating.ok && (
              <p style={{ fontSize: '0.8rem', color: '#c00' }}>{updating.msg}</p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmUpdate(false)}
                      style={buttonSecondaryStyle} disabled={updating === 'busy'}>Cancel</button>
              <button type="button" data-testid="ota-confirm"
                      disabled={updateText !== detail.name || updating === 'busy'}
                      onClick={submitUpdate}
                      style={{ ...buttonPrimaryStyle,
                               opacity: (updateText === detail.name && updating !== 'busy') ? 1 : 0.4 }}>
                {updating === 'busy' ? 'Updating\u2026' : `Update to ${versionLabel(ota.target_tag)}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmNodeDelete && detail && (
        <div style={overlayStyle} onClick={() => !deletingNode && setConfirmNodeDelete(false)}>
          <div style={confirmModalStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#c00' }}>Decommission node #{detail.nodeId}</h3>
            <p style={{ fontSize: '0.85rem' }}>
              This permanently removes <strong>{detail.name}</strong> from the hub —
              its node_link, node_prop, and node rows. There is no undo. The node's
              container is not stopped; do that separately.
            </p>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.83rem', marginBottom: 4 }}>
              <input type="checkbox" checked={delInclStations}
                     onChange={(e) => setDelInclStations(e.target.checked)} />
              Also delete its {detail.station_count} station(s)
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.83rem', marginBottom: 10 }}>
              <input type="checkbox" checked={delInclMember}
                     onChange={(e) => setDelInclMember(e.target.checked)} />
              Also delete its member identities (own + app-identity)
            </label>
            <p style={{ fontSize: '0.85rem' }}>
              Type the node name <code style={{ background: '#f5f5f5', padding: '1px 4px' }}>{detail.name}</code> to confirm:
            </p>
            <input value={nodeConfirmText} onChange={(e) => setNodeConfirmText(e.target.value)}
                   autoFocus style={{ ...inputStyle, width: '100%', marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmNodeDelete(false)}
                      style={buttonSecondaryStyle} disabled={deletingNode}>Cancel</button>
              <button type="button" disabled={nodeConfirmText !== detail.name || deletingNode}
                      onClick={submitNodeDelete}
                      style={{ ...buttonDangerStyle, opacity: (nodeConfirmText === detail.name && !deletingNode) ? 1 : 0.4 }}>
                {deletingNode ? 'Decommissioning…' : 'Decommission'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// The station picture is a large data URI; it drowns the probe view.
export function withoutImage(result) {
  const entry = result && Array.isArray(result.payload) ? result.payload[0] : null;
  if (!entry || !entry.config || !('image_url' in entry.config)) return result;
  const { image_url: _drop, ...config } = entry.config;
  return { ...result, payload: [{ ...entry, config }, ...result.payload.slice(1)] };
}

function AccordionSection({ title, open, onToggle, children }) {
  return (
    <div style={{ marginBottom: 8, borderTop: '1px solid #eee' }}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                 background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer',
                 fontSize: '0.85rem', color: '#555', textTransform: 'uppercase',
                 letterSpacing: '0.04em', fontWeight: 600, textAlign: 'left' }}>
        <span style={{ display: 'inline-block', width: 10, color: '#888' }}>{open ? '▾' : '▸'}</span>
        {title}
      </button>
      {open && children}
    </div>
  );
}

function StationTitle({ station: s, protectedHere }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {protectedHere && (
        <span title="Access-controlled (protected)"
              style={{ marginRight: 4, display: 'inline-flex', verticalAlign: 'middle' }}>
          <ShieldIcon width={12} height={12} />
        </span>
      )}
      <span>{s.name}</span>{' '}
      <span style={meta}>id={s.id}</span>
      {s.status !== 'ACTIVE' && (
        <span title="Disabled — not served until re-enabled" style={{
          marginLeft: 6, fontSize: '0.66rem', fontWeight: 600,
          color: '#8a4b4b', background: '#f6e8e8', border: '1px solid #e0c2c2',
          borderRadius: 3, padding: '1px 5px', verticalAlign: 'middle' }}>
          INACTIVE
        </span>
      )}
      {s.live_count > 0 && (
        <span title={`${s.live_count} live session${s.live_count === 1 ? '' : 's'}`}
          style={{
            marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '1px 6px 1px 5px', borderRadius: 8,
            background: '#e6f7ec', color: '#1b6b3a', fontSize: '0.72rem',
            fontWeight: 600, verticalAlign: 'middle',
          }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#1b6b3a', display: 'inline-block',
          }} />
          Live {s.live_count}
        </span>
      )}
    </div>
  );
}

// Only inline images: the form stores data URIs, and an arbitrary URL would make
// the admin fetch from wherever a station's config points.
export function stationImage(station) {
  const url = station && station.config && station.config.image_url;
  return typeof url === 'string' && url.startsWith('data:image/') ? url : null;
}

// Protection is inherited, so an unprotected station under a protected node or
// network is still gated.
export function stationAccess(isProtected, stationId, nodeId, networkId) {
  if (!isProtected) return { label: 'Unknown', detail: 'access lists unavailable' };
  if (isProtected('station', stationId)) return { label: 'Protected', detail: 'this station has an access list' };
  if (nodeId && isProtected('node', nodeId)) return { label: 'Protected', detail: 'inherited from its node' };
  if (networkId != null && isProtected('network', networkId)) {
    return { label: 'Protected', detail: 'inherited from its network' };
  }
  return { label: 'Open', detail: 'no access list on the station, its node or its network' };
}

// Slides out to the left of the node panel (portaled into App's host element).
// Any click outside it closes it, except on its own menu, a station row, or
// while one of its dialogs is open.
export function StationPanel({ station, nodeId, networkId, isProtected, modalOpen,
                               onClose, onProbe, menuItems }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [ownerName, setOwnerName] = useState(null);
  const latest = useRef({ modalOpen, onClose });
  latest.current = { modalOpen, onClose };

  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      if (latest.current.modalOpen) return;
      const t = e.target;
      if (ref.current && ref.current.contains(t)) return;
      if (t && t.closest && (t.closest('[role="menu"]') || t.closest('[data-station-row]'))) return;
      latest.current.onClose();
    };
    const onEsc = (e) => {
      if (e.key !== 'Escape' || latest.current.modalOpen) return;
      if (document.querySelector('[role="menu"]')) return;  // Esc closes the menu first
      latest.current.onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  // Names need manage_user_access; without it the owner shows as an account id.
  const ownerId = station.owner_account_id;
  useEffect(() => {
    setOwnerName(null);
    if (ownerId == null) return undefined;
    let live = true;
    fetch('/api/user-access/users', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        const u = ((b && b.users) || []).find((x) => String(x.account_id) === String(ownerId));
        if (live && u) setOwnerName(u.display_name || u.email || null);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [ownerId]);

  const access = stationAccess(isProtected, station.id, nodeId, networkId);
  return (
    <div ref={ref} role="dialog" aria-label={`Station ${station.name}`} data-testid="station-panel"
      style={{ ...stationPanel, transform: shown ? 'translateX(0)' : 'translateX(100%)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
        {stationImage(station) && (
          <img src={stationImage(station)} alt="" data-testid="station-image"
               style={{ width: 56, height: 56, borderRadius: 6, objectFit: 'cover',
                        border: '1px solid #eee', flex: '0 0 auto', marginRight: 4 }} />
        )}
        <h2 style={{ margin: 0, fontSize: '1.05rem', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
          {station.name}
        </h2>
        <HamburgerMenu items={menuItems} />
        <button type="button" onClick={onClose} aria-label="close station panel"
          style={{ ...hamburgerBtn, fontSize: '1rem' }}>×</button>
      </div>
      <div style={{ marginBottom: 12 }}>
        <StationTitle station={{ ...station, name: '' }} protectedHere={false} />
      </div>
      <div style={{ display: 'flex', gap: 6, margin: '0 0 16px' }}>
        <button type="button" onClick={onProbe} style={probeBtn}>probe</button>
      </div>
      <PanelField label="Description">
        {station.description
          ? <span style={{ whiteSpace: 'pre-wrap' }}>{station.description}</span>
          : <Empty>No description.</Empty>}
      </PanelField>
      <PanelField label="Owner">
        {ownerId == null ? <Empty>No owner.</Empty>
          : ownerName ? <span>{ownerName} <span style={meta}>account #{ownerId}</span></span>
            : <span>account #{ownerId}</span>}
      </PanelField>
      <PanelField label="Access">
        <span data-testid="station-access" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {access.label === 'Protected' && <ShieldIcon width={12} height={12} />}
          <strong>{access.label}</strong>
        </span>
        <div style={{ ...meta, marginLeft: 0 }}>{access.detail}</div>
      </PanelField>
    </div>
  );
}

function PanelField({ label, children }) {
  return (
    <div style={{ marginBottom: 14, fontSize: '0.85rem' }}>
      <div style={{ fontSize: '0.72rem', color: '#777', textTransform: 'uppercase',
                    letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
      {children}
    </div>
  );
}

const stationPanel = {
  position: 'absolute', top: 0, bottom: 0, right: 0, width: 340, boxSizing: 'border-box',
  background: '#fff', borderLeft: '1px solid #ddd', boxShadow: '-4px 0 12px rgba(0,0,0,0.08)',
  padding: 16, overflowY: 'auto', transition: 'transform 180ms ease-out',
};
const stationRow = {
  display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center',
  background: 'none', border: 'none', borderBottom: '1px solid #f0f0f0', borderRadius: 3,
  padding: '6px 4px', marginBottom: 2, cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit', color: 'inherit',
};
const stationRowActive = { background: '#eef5ff' };

function Empty({ children }) {
  return <div style={{ color: '#aaa', fontSize: '0.85rem', fontStyle: 'italic' }}>{children}</div>;
}

// Small dropdown menu for non-primary actions. items: array of
// { label, onClick, danger?, divider? }. Dismisses on item click,
// outside click, or Escape.
export function HamburgerMenu({ items }) {
  const [open, setOpen] = useState(false);
  // Anchor coords for the portaled menu (viewport-fixed). Computed from the
  // button's bounding rect at open time.
  const [coords, setCoords] = useState(null);
  // Inline styles don't support `:hover`, so track hovered item index
  // and swap in a background colour in render. -1 = none hovered.
  const [hoverIndex, setHoverIndex] = useState(-1);
  const ref = useRef(null);       // button wrapper (anchor + click-away)
  const menuRef = useRef(null);    // portaled menu (click-away)
  const openMenu = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setCoords({ top: r.bottom + 2, right: window.innerWidth - r.right });
    setOpen(true);
  };
  useEffect(() => {
    if (!open) {
      // Reset on close so a fresh open doesn't flash a stale highlight
      // before the user moves the mouse.
      setHoverIndex(-1);
      return;
    }
    const onClickAway = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    // The menu is portaled to fixed viewport coords; if the station/app
    // panel (overflow:auto) scrolls underneath it, close rather than let
    // it float detached. Capture-phase to catch the inner panel's scroll.
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);
  return (
    <div ref={ref} style={{ display: 'inline-block' }}>
      <button type="button" onClick={() => (open ? setOpen(false) : openMenu())}
        style={hamburgerBtn} aria-label="more actions" aria-haspopup="menu"
        aria-expanded={open}>⋯</button>
      {open && coords && ReactDOM.createPortal(
        // Portaled to <body> so the station/app panel's `overflow:auto`
        // can't clip it (core-network-tiers: the per-station kebab was
        // cut off by its own scrollable panel). Fixed-positioned at the
        // button's rect.
        <div ref={menuRef} role="menu"
          style={{ ...menuPanel, position: 'fixed', top: coords.top, right: coords.right }}>
          {items.map((it, i) => it.divider ? (
            <div key={i} style={menuDivider} />
          ) : (
            <button key={i} type="button" role="menuitem"
              onClick={() => { setOpen(false); it.onClick(); }}
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex((h) => (h === i ? -1 : h))}
              style={{
                ...(it.danger ? menuItemDanger : menuItem),
                ...(hoverIndex === i
                  ? (it.danger ? menuItemDangerHover : menuItemHover)
                  : null),
              }}>
              {it.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function ProbeResult({ probe, onClose }) {
  const [showRaw, setShowRaw] = useState(false);
  if (!probe) return null;

  // BFF shape: { ref, payload: [<adminbot response>] }. First payload
  // entry is the structured node/station response we enrich in
  // AdminBot.nodeResponse. Pre-probe-diagnostic nodes still return the
  // older shape (no jar_version etc.); the structured renderer falls
  // back to skipping absent fields, the raw-JSON toggle remains.
  const body = probe.result && Array.isArray(probe.result.payload)
    ? (probe.result.payload[0] || {}) : (probe.result || {});

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={probeModalBox} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 10px', background: '#eee', fontSize: '0.72rem',
                    color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em',
                    position: 'sticky', top: 0 }}>
        <span>probe response{body.kind ? ` (${body.kind})` : ''}</span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {probe.result && (
            <button type="button" onClick={() => setShowRaw((v) => !v)}
              style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer',
                       fontSize: '0.7rem', padding: '0 4px', textTransform: 'none', letterSpacing: 0 }}>
              {showRaw ? 'structured' : 'raw JSON'}
            </button>
          )}
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer',
                     fontSize: '0.9rem', padding: '0 4px', lineHeight: 1 }}
            aria-label="dismiss">×</button>
        </span>
      </div>
      <div style={{ padding: '8px' }}>
        {probe.loading && <Empty>Sending…</Empty>}
        {probe.error && <div style={{ color: '#c00', fontSize: '0.8rem' }}>{probe.error}</div>}
        {probe.result && showRaw && (
          <pre style={{ fontSize: '0.75rem', background: '#fff', padding: '6px',
                        borderRadius: '3px', overflow: 'auto', margin: 0,
                        maxHeight: '320px', border: '1px solid #e8e8e8' }}>
            {JSON.stringify(probe.result, null, 2)}
          </pre>
        )}
        {probe.result && !showRaw && (
          body.kind === 'node' ? <NodeProbeBody body={body} />
            : body.kind === 'station' ? <StationProbeBody body={body} />
            : <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                {JSON.stringify(probe.result, null, 2)}
              </pre>
        )}
      </div>
      </div>
    </div>
  );
}

/** Bundle A + B + C: node identity, loaded surface, last_errors.
 *  Each section gracefully degrades when its fields are absent
 *  (older nodes pre-probe-diagnostic). */
export function NodeProbeBody({ body }) {
  return (
    <div style={{ fontSize: '0.8rem' }}>
      <ProbeSection title="Identity">
        <ProbeRow label="ok" value={String(body.ok)} good={body.ok === true} bad={body.ok === false} />
        <ProbeRow label="name" value={body.name} />
        <ProbeRow label="node_id" value={body.node_id} mono />
        <ProbeRow label="router" value={String(body.router)} />
        <ProbeRow label="jar_version" value={body.jar_version || '—'} />
        <ProbeRow label="python_version" value={body.python_version || '—'} />
        <ProbeRow label="uptime" value={fmtUptime(body.uptime_sec)} />
        <ProbeRow label="parent_live" value={body.parent_live == null ? '—' : String(body.parent_live)}
          good={body.parent_live === true} bad={body.parent_live === false} />
        <ProbeRow label="stations / apps" value={`${body.station_count ?? '?'} / ${body.app_count ?? '?'}`} />
        <ProbeRow label="subscribers" value={String(body.subscriber_count ?? 0)} />
        {body.capabilities && body.capabilities.length > 0 && (
          <ProbeRow label="capabilities" value={body.capabilities.join(', ')} />
        )}
      </ProbeSection>

      {(body.os || body.cpu || body.memory) && (
        <ProbeSection title="Host">
          {body.os && (
            <ProbeRow label="os"
              value={`${body.os.name || '?'} ${body.os.version || ''} (${body.os.arch || '?'})`.trim()} />
          )}
          {body.cpu && (
            <ProbeRow label="cpu"
              value={`${body.cpu.cores ?? '?'} core${body.cpu.cores === 1 ? '' : 's'}${body.cpu.load_avg != null ? ` · load ${body.cpu.load_avg.toFixed(2)}` : ''}`} />
          )}
          {body.memory && (
            <ProbeRow label="memory"
              value={fmtMemory(body.memory)} />
          )}
        </ProbeSection>
      )}

      {(body.self_update != null || body.self_update_ready != null) && (
        <ProbeSection title="Self-update">
          {/* No updater means in-stack (redeploy the stack); an updater set to `off`
              is the node owner's choice. Different remedies, so two rows. */}
          <ProbeRow label="updater"
            value={body.self_update_ready ? 'installed' : 'none (in-stack: redeploy to update)'} />
          {body.self_update_ready && (
            <ProbeRow label="mode" value={body.self_update}
              good={body.self_update === 'auto'} />
          )}
          {/* The pending marker outlives a successful install, so it only means
              "offered but not running" while it is ahead of the jar. */}
          {isNewerVersion(body.self_update_pending, body.jar_version) && (
            <ProbeRow label="pending" value={`v${body.self_update_pending} (not installed)`}
              bad />
          )}
        </ProbeSection>
      )}

      <ProbeSection title="Loaded">
        {body.helpers_scope ? (
          <ProbeRow label="scope"
            value={`${body.helpers_scope.mode}${body.helpers_scope.list && body.helpers_scope.list.length ? ': ' + body.helpers_scope.list.join(', ') : ''}`} />
        ) : <ProbeRow label="scope" value="— (older node)" />}
        {body.helpers_loaded ? (
          <ProbeRow label={`helpers (${body.helpers_loaded.length})`}
            value={body.helpers_loaded.length ? body.helpers_loaded.join(', ') : '(none)'} wrap />
        ) : <ProbeRow label="helpers" value="—" />}
        {body.namespaces && (
          <ProbeRow label={`namespaces (${body.namespaces.length})`}
            value={body.namespaces.length ? body.namespaces.join(', ') : '(none)'} wrap />
        )}
      </ProbeSection>

      {body.last_errors != null && (
        <ProbeSection title={`Recent errors (${body.last_errors.length})`}>
          {body.last_errors.length === 0
            ? <div style={{ color: '#888', fontStyle: 'italic' }}>No errors captured since restart.</div>
            : <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #eee', background: '#fff', borderRadius: 3 }}>
                {body.last_errors.slice().reverse().map((e, i) => (
                  <div key={i} style={{ padding: '4px 6px', borderBottom: '1px solid #f4f4f4', fontSize: '0.75rem' }}>
                    <div style={{ color: '#888', fontSize: '0.7rem' }}>
                      {e.when || '?'} · <code>{e.function || '?'}</code>
                    </div>
                    <div style={{ color: '#c00' }}>
                      <strong>{e.exc_type || 'Exception'}</strong>: {e.message}
                    </div>
                  </div>
                ))}
              </div>
          }
        </ProbeSection>
      )}

      <div style={{ ...meta, marginTop: 6, fontSize: '0.7rem' }}>
        received_at {body.received_at || '?'}
      </div>
    </div>
  );
}

/** Per-station probe response — base fields are unchanged from pre-slug;
 *  rendered structured instead of as JSON. probe-augment slug also
 *  fetches the live-session list (best-effort; absent on access-denied
 *  or aveq-agent unreachable). */
function StationProbeBody({ body }) {
  const [sessions, setSessions] = useState(null);
  const [sessionsErr, setSessionsErr] = useState(null);
  useEffect(() => {
    if (!body.node_id || body.station_id == null) return;
    let cancelled = false;
    fetch(`/api/nodes/${body.node_id}/stations/${body.station_id}/sessions`,
          { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => { if (!cancelled) setSessions(j); })
      .catch((e) => { if (!cancelled) setSessionsErr(String(e.message || e)); });
    return () => { cancelled = true; };
  }, [body.node_id, body.station_id]);
  return (
    <div style={{ fontSize: '0.8rem' }}>
      <ProbeSection title="Station">
        <ProbeRow label="ok" value={String(body.ok)} good={body.ok === true} bad={body.ok === false} />
        <ProbeRow label="name" value={body.name} />
        <ProbeRow label="station_id" value={String(body.station_id)} mono />
        <ProbeRow label="status" value={body.status} />
        <ProbeRow label="local" value={String(body.local)} />
        <ProbeRow label="node_id" value={body.node_id} mono />
      </ProbeSection>
      {body.config && Object.keys(body.config).length > 0 && (
        <ProbeSection title="Config">
          {Object.entries(body.config).map(([k, v]) => (
            <ProbeRow key={k} label={k}
              value={typeof v === 'object' ? JSON.stringify(v) : String(v)} wrap />
          ))}
        </ProbeSection>
      )}
      <SessionsSection sessions={sessions} error={sessionsErr} />
      <div style={{ ...meta, marginTop: 6, fontSize: '0.7rem' }}>
        received_at {body.received_at || '?'}
      </div>
    </div>
  );
}

function SessionsSection({ sessions, error }) {
  if (error) {
    return (
      <ProbeSection title="Live sessions">
        <div style={{ color: '#888', fontStyle: 'italic', fontSize: '0.75rem' }}>
          unavailable ({error})
        </div>
      </ProbeSection>
    );
  }
  if (sessions == null) {
    return (
      <ProbeSection title="Live sessions">
        <div style={{ color: '#888', fontStyle: 'italic', fontSize: '0.75rem' }}>loading…</div>
      </ProbeSection>
    );
  }
  const total = sessions.total || 0;
  const rows = sessions.sessions || [];
  return (
    <ProbeSection title={`Live sessions (${total})`}>
      {total === 0 ? (
        <div style={{ color: '#888', fontStyle: 'italic', fontSize: '0.75rem' }}>
          No one's connected to this station right now.
        </div>
      ) : (
        <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee',
                      background: '#fff', borderRadius: 3 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ padding: '4px 6px', borderBottom: '1px solid #f4f4f4', fontSize: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong>{r.display_name}</strong>
                <span style={{ color: '#888' }}>idle {fmtIdle(r.idle_sec)}</span>
              </div>
              <div style={{ color: '#888', fontSize: '0.7rem' }}>
                started {fmtRelative(r.started_at)}
              </div>
            </div>
          ))}
          {rows.length < total && (
            <div style={{ padding: '4px 6px', fontSize: '0.7rem', color: '#888', fontStyle: 'italic' }}>
              showing {rows.length} of {total} (cap)
            </div>
          )}
        </div>
      )}
    </ProbeSection>
  );
}

function ProbeSection({ title, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase',
                    letterSpacing: '0.04em', marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

function ProbeRow({ label, value, mono, wrap, good, bad }) {
  const color = good ? '#0a0' : bad ? '#c00' : '#333';
  return (
    <div style={{ display: 'flex', gap: 8, padding: '1px 0' }}>
      <span style={{ flex: '0 0 110px', color: '#888' }}>{label}</span>
      <span style={{
        flex: 1, color,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : undefined,
        wordBreak: wrap ? 'break-word' : 'normal',
      }}>{value == null || value === '' ? '—' : value}</span>
    </div>
  );
}

// `1.208.2-SNAPSHOT` vs `1.195.1`: numeric compare of the first three parts.
// A version missing on either side is never "newer".
export function isNewerVersion(a, b) {
  const parse = (v) => {
    const m = /^v?(\d+)\.(\d+)\.(\d+)/.exec(String(v || '').trim());
    return m ? m.slice(1).map(Number) : null;
  };
  const pa = parse(a);
  const pb = parse(b);
  if (!pa || !pb) return false;
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] > pb[i];
  }
  return false;
}

function fmtUptime(sec) {
  if (sec == null || sec < 0) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
}

function fmtMemory(mem) {
  if (!mem || mem.total_bytes == null) return '—';
  const used = mem.total_bytes - (mem.free_bytes ?? 0);
  return `${fmtBytes(used)} / ${fmtBytes(mem.total_bytes)}`;
}

function fmtBytes(b) {
  if (b == null) return '?';
  const gib = b / (1024 ** 3);
  if (gib >= 1) return `${gib.toFixed(1)} GiB`;
  const mib = b / (1024 ** 2);
  return `${mib.toFixed(0)} MiB`;
}

function fmtIdle(sec) {
  if (sec == null) return '?';
  if (sec < 1) return 'now';
  if (sec < 60) return `${Math.floor(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}

function fmtRelative(epochSec) {
  if (epochSec == null) return '?';
  const ageS = Date.now() / 1000 - epochSec;
  if (ageS < 60) return `${Math.floor(ageS)}s ago`;
  if (ageS < 3600) return `${Math.floor(ageS / 60)}m ago`;
  if (ageS < 86400) return `${Math.floor(ageS / 3600)}h ago`;
  return `${Math.floor(ageS / 86400)}d ago`;
}

const item = { fontSize: '0.85rem', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #f0f0f0' };
const meta = { color: '#888', fontSize: '0.78rem' };
const probeBtn = {
  background: '#5fa67a', color: '#fff', border: 'none', borderRadius: '4px',
  padding: '5px 12px', fontSize: '0.78rem', cursor: 'pointer',
};
const hamburgerBtn = {
  background: 'transparent', color: '#666', border: 'none',
  padding: '0 6px', fontSize: '1.1rem', lineHeight: 1, cursor: 'pointer',
};
const menuPanel = {
  // Base look; HamburgerMenu portals this to <body> and overrides
  // position→fixed + top/right from the button's rect (so the panel's
  // overflow:auto can't clip it). zIndex above panels, below modals (1000).
  minWidth: 140,
  background: '#fff', border: '1px solid #ccc', borderRadius: 4,
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)', padding: '4px 0', zIndex: 200,
};
const menuItem = {
  display: 'block', width: '100%', textAlign: 'left',
  background: 'transparent', border: 'none', cursor: 'pointer',
  padding: '5px 12px', fontSize: '0.78rem', color: '#333',
};
const menuItemDanger = { ...menuItem, color: '#c00' };
// Hover backgrounds layered in render via spread; danger row uses a
// faint red wash so the destructive cue carries through the highlight.
const menuItemHover = { background: '#f0f0f0' };
const menuItemDangerHover = { background: '#fbe9e9' };
const menuDivider = {
  height: 1, background: '#eee', margin: '4px 0',
};
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const confirmModalStyle = {
  background: '#fff', padding: 20, borderRadius: 6, minWidth: 360, maxWidth: 480,
  boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};
// core-network-tiers slug: probe result as a popup (was inline). No padding —
// the sticky header bar spans full width; the body has its own padding.
const probeModalBox = {
  background: '#fff', borderRadius: 6, minWidth: 380, maxWidth: 560,
  maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};
const inputStyle = {
  padding: '6px 8px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 4,
};
const buttonSecondaryStyle = {
  padding: '8px 16px', background: 'transparent', color: '#5fa67a',
  border: '1px solid #5fa67a', borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
// node-ota-update: the confirm action is PRIMARY, not danger. An update is not
// destructive in the way decommission is — it is reversible by re-deploying the
// old tag and clicking again (ADR D4's rollback path), and the `.env.bak` keeps
// the breadcrumb. Red would overstate it and make the genuinely irreversible
// button next to it mean less.
const buttonPrimaryStyle = {
  padding: '8px 16px', background: '#5fa67a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
const buttonDangerStyle = {
  padding: '8px 16px', background: '#c00', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
